import {
  AddFill18Icon,
  CloseOutline18Icon,
  StarsOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { FC, ReactNode, useState } from "react";
import {
  IconButton,
  Accordion,
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
  Flex,
  Text,
  tokens,
} from "shared/components/Spor";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

type ActionCardProps = {
  title: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  primaryAction?: ReactNode;
  tertiaryAction?: ReactNode;
  defaultExpanded?: boolean;
  className?: string;
};

const Card = styled(Accordion)`
  width: 100%;
`;

const CardItem = styled(AccordionItem)<{ $expanded: boolean }>`
  overflow: hidden;
  border-radius: 12px;
  color: ${({ theme }) => theme.colorTextMain};
  background: transparent;
  border: ${({ $expanded }) =>
    $expanded
      ? "1px solid var(--spor-colors-outline-disabled, rgba(255, 255, 255, 0.1))"
      : "none"};

  &&& {
    outline: none;
  }

  &&&& .spor-accordion__itemBody {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    margin-top: 0;
  }

  &&&& .spor-accordion__itemTrigger:hover {
    filter: none;
  }
`;

const CardHeaderRow = styled.div<{ $expanded: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: 12px 12px 0 0;
  background: ${({ $expanded, theme }) => {
    if (theme.themeName === "light") {
      return $expanded ? "transparent" : "rgba(0, 0, 0, 0.05)";
    }
    if ($expanded) return "transparent";
    return "var(--spor-colors-surface-disabled)";
  }};

  /* REVIEW-SPOR: Recheck the full Accordion header hover surface when Spor's hover token matches the spec. */
  &:hover {
    background: ${({ theme }) =>
      theme.themeName === "light"
        ? "rgba(178, 223, 215, 1)"
        : "rgba(0, 69, 62, 1)"};
  }

  > h3 {
    flex: 1;
    min-width: 0;

    > button {
      background: transparent !important;

      &:hover,
      &:active,
      &:focus {
        background: transparent !important;
      }
    }
  }
`;

const CardHeader = styled(AccordionItemTrigger)`
  &&& {
    min-height: 40px;
    gap: ${tokens.default.size.spacing[1.5]};
    padding: ${tokens.default.size.spacing[2]} ${tokens.default.size.spacing[8]}
      ${tokens.default.size.spacing[2]} ${tokens.default.size.spacing[2]};
    border: 0;
    border-radius: 0;
    background: transparent;
    color: ${({ theme }) => theme.colorTextMain};
    text-align: left;
    transition: background-color 120ms ease;
  }

  &&&:hover {
    background: transparent;
    outline: none;
  }

  &&&:active {
    outline: none;
  }

  &&&:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colorOutlineFocus};
    outline-offset: -2px;
  }
`;

const CardToggle = styled(IconButton)`
  flex-shrink: 0;
  margin-right: ${tokens.default.size.spacing[2]};

  &&& {
    background: transparent !important;
  }

  &&&:hover,
  &&&:active,
  &&&:focus {
    background: transparent !important;
  }
`;

const CardContent = styled(AccordionItemContent)<{ $expanded: boolean }>`
  overflow: hidden;
  padding: 0;
  border-radius: 0;
  border-top: ${({ $expanded }) =>
    $expanded
      ? "1px solid var(--spor-colors-outline-disabled, rgba(255, 255, 255, 0.1))"
      : "none"};

  &[data-state="open"] {
    animation: action-card-expand 220ms ease-out;
  }

  &[data-state="closed"] {
    animation: action-card-collapse 180ms ease-out;
  }

  @keyframes action-card-expand {
    from {
      height: 0;
      opacity: 0;
    }
    to {
      height: var(--height);
      opacity: 1;
    }
  }

  @keyframes action-card-collapse {
    from {
      height: var(--height);
      opacity: 1;
    }
    to {
      height: 0;
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

const CardBody = styled.div`
  display: flex;
  min-height: 136px;
  flex-direction: column;
  gap: 12px;
  padding: ${tokens.default.size.spacing[2]};
`;

export const ActionCard: FC<ActionCardProps> = ({
  title,
  icon = <StarsOutline24Icon aria-hidden="true" />,
  children,
  primaryAction,
  tertiaryAction,
  defaultExpanded = false,
  className,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card
      className={className}
      collapsible
      // Mount fields when visible so Spor can measure their floating labels.
      lazyMount
      value={expanded ? ["action-card"] : []}
      onValueChange={({ value }) => setExpanded(value.includes("action-card"))}
      variant="core"
    >
      <CardItem value="action-card" $expanded={expanded}>
        <CardHeaderRow data-action-card-header $expanded={expanded}>
          <CardHeader headingLevel="h3" showChevron={false}>
            <Flex alignItems="center" flex="1" gap="1.5">
              {icon}
              <Text as="span" variant="sm-label-bold">
                {title}
              </Text>
            </Flex>
          </CardHeader>
          <CardToggle
            aria-expanded={expanded}
            aria-label={t(
              expanded ? "Lukk handlingskort" : "Åpne handlingskort",
            )}
            icon={
              expanded ? (
                <CloseOutline18Icon aria-hidden="true" />
              ) : (
                <AddFill18Icon aria-hidden="true" />
              )
            }
            onClick={() => setExpanded((isExpanded) => !isExpanded)}
            size="xs"
            variant="tertiary"
          />
        </CardHeaderRow>
        <CardContent data-action-card-content $expanded={expanded}>
          <CardBody style={{ border: "none" }}>
            {children}
            {(tertiaryAction || primaryAction) && (
              <Flex
                alignItems="center"
                gap={tokens.default.size.spacing[1.5]}
                justifyContent="flex-end"
                mt="auto"
              >
                {tertiaryAction}
                {primaryAction}
              </Flex>
            )}
          </CardBody>
        </CardContent>
      </CardItem>
    </Card>
  );
};
